import React, { FC, useEffect } from 'react';

import Api from '~/Api';
import { PRODUCTS_PROJECT_CONFIG } from '~/constants/products';
import { useModalContext } from '~/contexts/modal/ModalContext';
import useErrors from '~/hooks/useErrors';
import {
  DEFAULT_INPUT_CURRENT,
  DEFAULT_INPUT_INCREASER,
  DEFAULT_INPUT_LIMIT,
  DEFAULT_NEW_LIMIT,
  DEFAULT_PLAGIARISM_RESULT,
  featureUnlockModalConfig,
  PREMIUM_FEATURE_PURCHASE_SOURCE,
} from '~/modules/feature-access/constants';
import { useFeatureAccess } from '~/modules/feature-access/hooks/useFeatureAccess';
import DetectorHighlights from '~/modules/feature-access/modals/FeatureUnlockModal/DetectorHighlights';
import DetectorReport from '~/modules/feature-access/modals/FeatureUnlockModal/DetectorReport';
import HumanizerGoal from '~/modules/feature-access/modals/FeatureUnlockModal/HumanizerGoal';
import InputLimitOverflow from '~/modules/feature-access/modals/FeatureUnlockModal/InputLimitOverflow';
import ParaphraserLevel from '~/modules/feature-access/modals/FeatureUnlockModal/ParaphraserLevel';
import ParaphraserOutput from '~/modules/feature-access/modals/FeatureUnlockModal/ParaphraserOutput';
import ParaphraserTone from '~/modules/feature-access/modals/FeatureUnlockModal/ParaphraserTone';
import PlagiarismReport from '~/modules/feature-access/modals/FeatureUnlockModal/PlagiarismReport';
import {
  FeatureDenyParams,
  FeatureKey,
  FeatureModule,
} from '~/modules/feature-access/policy';
import analytics from '~/services/analytics';
import { featureAccessService } from '~/services/featureAccess/FeatureAccess';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { timeout } from '~/utils/common';

import FeatureUnlockModal from './modals/FeatureUnlockModal';

type FeatureRegistrationConfig = {
  key: FeatureKey;
  // TODO! Upgrade props typing
  getIntroComponent: (props?: Record<string, any>) => FC;
};

const FEATURE_REGISTRATIONS: FeatureRegistrationConfig[] = [
  {
    key: FeatureKey.DETECTOR_DETAILED_REPORT,
    getIntroComponent: (props) => () => <DetectorReport {...props} />,
  },
  {
    key: FeatureKey.DETECTOR_INPUT_HIGHLIGHTS,
    getIntroComponent: (props) => () => <DetectorHighlights {...props} />,
  },
  {
    key: FeatureKey.DETECTOR_INPUT_LIMIT_OVERFLOW,
    getIntroComponent: (props) => () => (
      <InputLimitOverflow
        featureKey={FeatureKey.DETECTOR_INPUT_LIMIT_OVERFLOW}
        featureModule={FeatureModule.AI_CONTENT_DETECTOR}
        increaser={props?.increaser || DEFAULT_INPUT_INCREASER}
        inputLimit={props?.inputLimit || DEFAULT_INPUT_LIMIT}
        inputCurrent={props?.inputCurrent || DEFAULT_INPUT_CURRENT}
        newLimit={props?.newLimit || DEFAULT_NEW_LIMIT}
      />
    ),
  },
  {
    key: FeatureKey.PLAGIARISM_DETAILED_REPORT,
    getIntroComponent: (props) => () => (
      <PlagiarismReport
        plagiarismResult={props?.plagiarismResult || DEFAULT_PLAGIARISM_RESULT}
      />
    ),
  },
  {
    key: FeatureKey.PLAGIARISM_INPUT_LIMIT_OVERFLOW,
    getIntroComponent: (props) => () => (
      <InputLimitOverflow
        featureKey={FeatureKey.PLAGIARISM_INPUT_LIMIT_OVERFLOW}
        featureModule={FeatureModule.PLAGIARISM_CHECK}
        increaser={props?.increaser || DEFAULT_INPUT_INCREASER}
        inputLimit={props?.inputLimit || DEFAULT_INPUT_LIMIT}
        inputCurrent={props?.inputCurrent || DEFAULT_INPUT_CURRENT}
        newLimit={props?.newLimit || DEFAULT_NEW_LIMIT}
      />
    ),
  },
  {
    key: FeatureKey.HUMANIZER_GOAL,
    getIntroComponent: (props) => () => (
      <HumanizerGoal humanizerMode={props?.humanizerMode} />
    ),
  },
  {
    key: FeatureKey.PARAPHRASER_LEVEL,
    getIntroComponent: (props) => () => (
      <ParaphraserLevel paraphraserLevel={props?.paraphraserLevel} />
    ),
  },
  {
    key: FeatureKey.PARAPHRASER_OUTPUT_COUNTER,
    getIntroComponent: (props) => () => <ParaphraserOutput {...props} />,
  },
  {
    key: FeatureKey.PARAPHRASER_TONE_OF_VOICE,
    getIntroComponent: (props) => () => <ParaphraserTone {...props} />,
  },
];

type Props = {
  children?: React.ReactNode;
};

export const FeatureAccessInitializer: FC<Props> = ({ children }) => {
  const { handleOpenModal } = useModalContext();
  const { reportUserErrors } = useErrors();
  const { user_product, user_policy } = useAppSelector(
    (state) => state.profile,
  );

  const { loadAndDispatchActualPolicyData } = useActions();
  const { getFeatureAccess } = useFeatureAccess();

  const upgradeSubscription = async (
    IntroComponent: FC,
    premiumFeature: FeatureKey,
    // TODO! temporary add this props at 1674 task. Think about changing logic
    onDenyProps?: FeatureDenyParams,
  ): Promise<void> => {
    try {
      const product = await Api.getProductById(
        PRODUCTS_PROJECT_CONFIG.ANNUAL_SUB_OFFER_ID,
      );

      handleOpenModal({
        onClose: () => {
          analytics.trackEvent('platform checkout - close', {
            purchase_source: PREMIUM_FEATURE_PURCHASE_SOURCE,
            premium_feature: premiumFeature,
            ...(onDenyProps && onDenyProps.toolTitle
              ? { tool: onDenyProps.toolTitle }
              : {}),
          });
        },
        ...featureUnlockModalConfig,
        component: ({ onClose }) => (
          <FeatureUnlockModal
            products={[product]}
            onSuccessPayment={async () => {
              await timeout(4000);
              await loadAndDispatchActualPolicyData();
              onClose();
              if (onDenyProps && onDenyProps.onAccessUnlock) {
                onDenyProps.onAccessUnlock();
              }
            }}
            IntroComponent={IntroComponent}
            premiumFeature={premiumFeature}
            {...(onDenyProps && onDenyProps.toolTitle
              ? { toolTitle: onDenyProps.toolTitle }
              : {})}
          />
        ),
      });
    } catch (e: any) {
      reportUserErrors({
        error: e,
        method: 'getProductById',
        userMessage: 'Unable to fetch product details. Please retry or',
      });
    }
  };

  useEffect(() => {
    const features = user_policy?.features ?? {};

    Object.values(FeatureKey).forEach((key) => {
      const hasAccess = Object.values(features).some(
        (group) => group?.[key] === true,
      );
      featureAccessService.updateAccess(key, hasAccess);
    });
  }, [user_policy.features, user_product.id]);

  useEffect(() => {
    const isNotPremium = !getFeatureAccess(
      FeatureModule.AI_CONTENT_DETECTOR,
      FeatureKey.DETECTOR_INPUT_LIMIT_OVERFLOW,
    ); // any feature. since now we unlock all features at once

    if (isNotPremium) {
      FEATURE_REGISTRATIONS.forEach(({ key, getIntroComponent }) => {
        featureAccessService.registerFeature({
          key,
          onDeny: async (props) => {
            const IntroComponent = getIntroComponent(props);
            await upgradeSubscription(IntroComponent, key, props);
          },
        });
      });
    }
  }, [user_product.id]);

  return <>{children}</>;
};
